/**
 * Family & members → Phone logins (owner only).
 * The owner makes a user name + password for a family member, e.g. an older
 * parent, so they can sign in to the Bhorosha phone app without an email.
 * Shows a login card to print or share, with a QR code that fills both in.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import { backend } from '../data';
import { PEOPLE_KEY, USERNAME_RE, type AppLogin, type BhStore, type Role } from '@bhorosha/data';
import { t, tErr } from '@bhorosha/i18n';
import { ErrorText, Field, Notice, Spinner, initials } from '../ui/components';
import { fmtDMYTime } from '../ui/dates';

type Person = { id: string; name: string; notify_user_ids?: string[] };
type Card = { name: string; username: string; password: string };

const APP_URL = (import.meta.env.VITE_MOBILE_APP_URL as string | undefined) || '';

/** Suggest a user name from a name: "Saiara Begum" -> "saiara" */
function suggestUsername(name: string) {
  const first = name.trim().toLowerCase().split(/\s+/)[0] || '';
  return first.normalize('NFKD').replace(/[^a-z0-9._-]/g, '').slice(0, 30);
}

export function signInLink(username: string, password: string) {
  return `bhorosha://signin?u=${encodeURIComponent(username)}&p=${encodeURIComponent(password)}`;
}

/** `store` is the family's data (to add the login to "Who gets reminders"); the super admin console has none. */
export function AppLoginsTab({ fid, store, people, onChanged }: { fid: string; store?: BhStore; people: Person[]; onChanged: () => Promise<unknown> }) {
  const [logins, setLogins] = useState<AppLogin[] | null>(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [card, setCard] = useState<Card | null>(null);
  const [adding, setAdding] = useState(false);

  const reload = useCallback(async () => {
    try { setLogins(await backend.listAppLogins(fid)); } catch (e) { setErr(tErr(e)); setLogins([]); }
  }, [fid]);
  useEffect(() => { void reload(); }, [reload]);

  async function act(fn: () => Promise<unknown>, ok: string) {
    setErr(''); setMsg('');
    try { await fn(); await reload(); await onChanged(); setMsg(ok); } catch (e) { setErr(tErr(e)); }
  }

  /** Make sure the new login gets reminders for the people chosen in the dialog. */
  function addToNotifyLists(userId: string, personIds: string[]) {
    if (!store) return;
    try {
      const all = JSON.parse(store.getItem(PEOPLE_KEY) || '[]') as Person[];
      let changed = false;
      all.forEach(p => {
        if (personIds.includes(p.id) && Array.isArray(p.notify_user_ids) && !p.notify_user_ids.includes(userId)) {
          p.notify_user_ids = [...p.notify_user_ids, userId]; changed = true;
        }
      });
      if (changed) { store.setItem(PEOPLE_KEY, JSON.stringify(all)); window.__bhReload?.(); }
    } catch { /* ignore: can be set in Edit person */ }
  }

  return (
    <div>
      <h3 className="bh-h3">{t('app.title')}</h3>
      <p className="bh-small bh-muted">{t('app.help')}</p>
      {msg && <Notice tone="ok">{msg}</Notice>}
      <ErrorText>{err}</ErrorText>
      {card && <LoginCard card={card} onClose={() => setCard(null)} />}

      {!adding && <button className="bh-btn bh-btn-primary" onClick={() => { setAdding(true); setCard(null); }}>{t('app.add')}</button>}
      {adding && (
        <AddLogin fid={fid} people={people} onCancel={() => setAdding(false)} setErr={setErr}
          onCreated={async (c, userId, personIds) => {
            addToNotifyLists(userId, personIds);
            setAdding(false); setCard(c); await reload(); await onChanged();
          }} />
      )}

      <h3 className="bh-h3">{t('app.list')}</h3>
      {logins === null ? <Spinner /> : !logins.length ? <p className="bh-small bh-muted">{t('app.none')}</p> : logins.map(l => (
        <div className="bh-member" key={l.userId}>
          <span className="bh-acct-avatar">{initials(l.displayName)}</span>
          <div className="bh-member-main">
            <div className="bh-strong">{l.displayName} {l.disabled && <span className="bh-chip bh-warn">{t('app.off')}</span>}</div>
            <div className="bh-small">{t('app.username')}: <b>{l.username}</b></div>
            <div className="bh-small bh-muted">{l.lastSignInAt ? t('app.lastSignIn', { when: fmtDMYTime(l.lastSignInAt) }) : t('app.neverSignedIn')}</div>
          </div>
          <div className="bh-member-side">
            <button className="bh-link" onClick={() => {
              const typed = prompt(t('app.resetPrompt', { name: l.displayName }), '');
              if (typed === null) return;
              void act(async () => {
                const r = await backend.appLoginAction({ action: 'family_reset_password', familyId: fid, userId: l.userId, password: typed.trim() || undefined });
                setCard({ name: l.displayName, username: l.username, password: r.password || typed.trim() });
              }, t('app.resetDone'));
            }}>{t('app.reset')}</button>
            <button className="bh-link" onClick={() => act(() => backend.appLoginAction({ action: 'family_disable_login', familyId: fid, userId: l.userId, disabled: !l.disabled }), l.disabled ? t('app.onDone') : t('app.offDone'))}>
              {l.disabled ? t('app.turnOn') : t('app.turnOff')}
            </button>
            <button className="bh-link bh-warn" onClick={() => {
              if (confirm(t('app.removeConfirm', { name: l.displayName }))) void act(() => backend.appLoginAction({ action: 'family_delete_login', familyId: fid, userId: l.userId }), t('app.removed'));
            }}>{t('app.remove')}</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AddLogin({ fid, people, onCancel, onCreated, setErr }: {
  fid: string; people: Person[]; onCancel: () => void; setErr: (s: string) => void;
  onCreated: (c: Card, userId: string, personIds: string[]) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [touchedUser, setTouchedUser] = useState(false);
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Exclude<Role, 'owner'>>('editor');
  const [all, setAll] = useState(false);
  const [ids, setIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const userOk = USERNAME_RE.test(username);

  async function submit(e: FormEvent) {
    e.preventDefault(); setErr('');
    if (!userOk) { setErr(t('app.err.username')); return; }
    if (!all && !ids.length) { setErr(t('fam.invite.pickPatient')); return; }
    if (password && password.length < 8) { setErr(t('app.err.password')); return; }
    setBusy(true);
    try {
      const r = await backend.appLoginAction({
        action: 'family_create_login', familyId: fid, username, name: name.trim() || username,
        password: password || undefined, role, allPatients: all, patientIds: all ? [] : ids,
      });
      await onCreated({ name: name.trim() || username, username: r.username || username, password: r.password || password }, r.userId!, all ? people.map(p => p.id) : ids);
    } catch (ex) { setErr(tErr(ex)); } finally { setBusy(false); }
  }

  return (
    <form className="bh-form bh-app-add" onSubmit={submit}>
      <Field label={t('app.name')}>
        <input value={name} required autoFocus placeholder="e.g. Saiara Begum"
          onChange={e => { setName(e.target.value); if (!touchedUser) setUsername(suggestUsername(e.target.value)); }} />
      </Field>
      <Field label={t('app.username')} hint={t('app.usernameHint')}>
        <input value={username} required autoCapitalize="none" autoCorrect="off" spellCheck={false}
          onChange={e => { setTouchedUser(true); setUsername(e.target.value.toLowerCase().replace(/\s+/g, '')); }} />
        {username && !userOk && <span className="bh-field-hint bh-warn">{t('app.err.username')}</span>}
      </Field>
      <Field label={t('app.password')} hint={t('app.passwordHint')}>
        <input value={password} onChange={e => setPassword(e.target.value)} placeholder={t('app.passwordAuto')} autoComplete="off" />
      </Field>
      <Field label={t('app.canSee')}>
        <div className="bh-access">
          <div className="bh-checks">
            {people.map(p => (
              <label key={p.id} className="bh-check">
                <input type="checkbox" checked={all || ids.includes(p.id)} disabled={all}
                  onChange={e => setIds(e.target.checked ? [...ids, p.id] : ids.filter(x => x !== p.id))} />
                {p.name || 'Unnamed'}
              </label>
            ))}
          </div>
          <label className="bh-check"><input type="checkbox" checked={all} onChange={e => setAll(e.target.checked)} /> {t('app.everyone')}</label>
        </div>
      </Field>
      <Field label={t('app.canDo')}>
        <label className="bh-radio"><input type="radio" checked={role === 'editor'} onChange={() => setRole('editor')} /> {t('app.role.editor')}</label>
        <label className="bh-radio"><input type="radio" checked={role === 'viewer'} onChange={() => setRole('viewer')} /> {t('app.role.viewer')}</label>
      </Field>
      <div className="bh-row">
        <button className="bh-btn bh-btn-primary" disabled={busy}>{busy ? t('common.loading') : t('app.create')}</button>
        <button type="button" className="bh-btn bh-btn-ghost" onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </form>
  );
}

function LoginCard({ card, onClose }: { card: Card; onClose: () => void }) {
  const [qr, setQr] = useState('');
  useEffect(() => { QRCode.toDataURL(signInLink(card.username, card.password), { margin: 1, width: 220 }).then(setQr).catch(() => setQr('')); }, [card]);
  const text = t('app.shareText', { name: card.name, username: card.username, password: card.password, url: APP_URL || t('app.getApp') });

  function print() {
    const w = window.open('', '_blank', 'width=480,height=640');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Bhorosha login</title>
      <style>body{font-family:Segoe UI,Arial,sans-serif;text-align:center;padding:24px;color:#1F2A3A}
      .c{border:2px dashed #9FB0C8;border-radius:16px;padding:20px;max-width:360px;margin:0 auto}
      h1{margin:0;font-size:26px}.m{color:#5E6B7D}.big{font-size:28px;font-weight:800;margin:2px 0 14px;letter-spacing:.03em}</style></head>
      <body><div class="c"><h1>Bhorosha</h1><div class="m">for ${escapeHtml(card.name)}</div>
      ${qr ? `<img src="${qr}" width="200" height="200" alt="">` : ''}<div class="m" style="font-size:13px">Scan with the phone camera to sign in</div>
      <div class="m" style="margin-top:14px">User name</div><div class="big">${escapeHtml(card.username)}</div>
      <div class="m">Password</div><div class="big">${escapeHtml(card.password)}</div>
      <div class="m" style="font-size:13px">${escapeHtml(APP_URL || t('app.getApp'))}</div></div>
      <script>window.onload=()=>{window.print()}</script></body></html>`);
    w.document.close();
  }
  async function share() {
    try { await navigator.share({ title: 'Bhorosha login', text }); }
    catch { try { await navigator.clipboard.writeText(text); alert(t('common.copied')); } catch { prompt('Copy', text); } }
  }

  return (
    <div className="bh-login-card">
      <div className="bh-login-card-inner">
        <div className="bh-strong" style={{ fontSize: 18 }}>Bhorosha</div>
        <div className="bh-small bh-muted">{t('app.cardFor', { name: card.name })}</div>
        {qr && <img src={qr} width={160} height={160} alt={t('app.qrAlt')} />}
        <div className="bh-small bh-muted">{t('app.username')}</div>
        <div className="bh-login-big">{card.username}</div>
        <div className="bh-small bh-muted">{t('app.password')}</div>
        <div className="bh-login-big">{card.password}</div>
      </div>
      <Notice tone="warn">{t('app.cardWarn')}</Notice>
      <div className="bh-row">
        <button className="bh-btn bh-btn-primary" onClick={print}>🖨 {t('app.print')}</button>
        <button className="bh-btn bh-btn-ghost" onClick={share}>{t('common.share')}</button>
        <button className="bh-btn bh-btn-ghost" onClick={onClose}>{t('common.close')}</button>
      </div>
    </div>
  );
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
